import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import * as XLSX from 'xlsx-js-style';
import { environment } from '../../../../environments/environment';
import {
  selectColumn,
  groupBy,
  applyMathFunction,
  difference,
  renameColumn,
  retrieveObject,
  retrieveArray,
  mapping,
  extractName,
} from './operation';
import { RequestService } from 'juboo-result';
@Injectable({
  providedIn: 'root',
})
export class ExportbatchService {
  private http = inject(HttpClient);
  private requestService = inject(RequestService);
  private apiUrl = environment.apiUrl;
  recap = signal<any>([]);
  isLoading = signal<boolean>(false);
  dictData = signal<any>([]);
  configurations = signal<any>([]);

  getDictData(categoryCode: string): Promise<Record<string, string>> {
    return this.http
      .get<any[]>(`${this.apiUrl}/categories/${categoryCode}/instances?by=CODE`)
      .toPromise()
      .then((res: any) => {
        const dictionary: Record<string, string> = {};
        res.forEach((item: any) => {
          const code = item.code;
          const name = item.name;
          if (name) {
            dictionary[code] = name;
          } else {
            console.warn('Missing name in item:', item);
          }
        });
        this.dictData.set(dictionary);
        return dictionary;
      })
      .catch((err) => {
        console.error('Dictionary load failed:', err);
        this.dictData.set({});
        return {};
      });
  }

  public async applyConfig(report: any, config: any) {
    let transformedData = report.results;
    if (report.results == null) {
      transformedData = await this.requestService.fetchAndDecompressZstdJson(report.resultURL).toPromise();
    }
    for (const operation of config.transformations.operations) {
      switch (operation.function) {
        case 'selectColumn':
          transformedData = selectColumn(transformedData, operation.parameter);
          break;
        case 'groupBy':
          transformedData = groupBy(
            transformedData,
            operation.column,
            operation.parameter
          );
          break;
        case 'difference':
          transformedData = difference(
            transformedData,
            operation.column,
            operation.parameter
          );
          break;
        case 'applyMathFunction':
          transformedData = applyMathFunction(
            transformedData,
            operation.column,
            operation.parameter
          );
          break;
        case 'renameColumn':
          transformedData = renameColumn(transformedData, operation.parameter);
          break;
        case 'retrieveObject':
          transformedData = retrieveObject(
            transformedData,
            operation.column,
            operation.parameter,
            report
          );
          break;
        case 'retrieveArray':
          transformedData = retrieveArray(
            transformedData,
            operation.column,
            operation.parameter,
            report
          );
          break;
        case 'extractName':
          transformedData = extractName(
            transformedData,
            operation.column,
            operation.parameter
          );
          break;
        case 'mapping': {
          const dict = await this.getDictData(operation.parameter.dictReference);
          transformedData = mapping(transformedData, operation.column, {
            dictData: dict,
          });
          break;
        }
        default:
          console.warn(`Unknown function: ${operation.function}`);
      }
    }

    return transformedData;
  }

  public applyFinalConfig(data: any, config: any) {
    let transformedData = data;
    if (config.transformations.additionalRows) {
      return transformedData;
    }
  }

  async exportXls(reconciliations: any, operationType: any): Promise<void> {
    this.isLoading.set(true);
    let reconciliationSheets: {
      [key: string]: {
        data: any[];
        internalSourceName: string;
        externalSourceName: string;
      };
    } = {};

    let reportsData: any = [];
    const configParsed = JSON.parse(operationType.configuration);
    const system_selector = configParsed.system_selector;
    if (system_selector === 'SERVICE') this.configurations.set(configParsed.reports);
    else this.configurations.set(configParsed);

    const validReconciliations = reconciliations.filter(
      (rec: any) => rec.id != null && rec.id !== ''
    );
    const promises = validReconciliations.map(async (rec: any) => {
      try {
        const res: any = await this.http
          .get(`${this.apiUrl}/reconciliations/reports/${rec.id}`)
          .toPromise();
        if (!res || !res.reconciliation) {
          console.error('Invalid response:', res);
          return;
        }

        const externalSourceName = res.reconciliation.source1.name.replace(/^\w+\s*/, '');
        const internalSourceName = res.reconciliation.source2.name
          .replace(/^(\w+)\s+\w+(\s+)/i, '$1$2')
          .trim();

        for (let j = 0; j < this.configurations().length; j++) {
          const transformed = await this.applyConfig(res, this.configurations()[j]);

          if (!reportsData[j]) {
            reportsData[j] = [];
          }

          reportsData[j].push(...transformed);
        }

        let results = res.results;
        if (res.results == null) {
          results = await this.requestService.fetchAndDecompressZstdJson(res.resultURL).toPromise();
        }
        const mismatchedResults = results.filter((r: any) => r.status === 'MISMATCH');
        const missingResults = results.filter((r: any) => r.status === 'MISSING');

        if (mismatchedResults.length > 0 || missingResults.length > 0) {
          reconciliationSheets[externalSourceName] = {
            data: [...mismatchedResults, ...missingResults],
            internalSourceName,
            externalSourceName,
          };
        }
      } catch (error) {
        console.error(`Error fetching reconciliation data for ID ${rec.id}:`, error);
      }
    });

    await Promise.all(promises);

    for (let j = 0; j < this.configurations().length; j++) {
      reportsData[j] = this.applyFinalConfig(reportsData[j], this.configurations()[j]);
    }

    this.exportExcel(operationType?.name, reportsData, reconciliationSheets);
    this.isLoading.set(false);
  }

  exportExcel(
    operationType: string,
    reports: any,
    reconciliationSheets: {
      [key: string]: {
        data: any[];
        internalSourceName: string;
        externalSourceName: string;
      };
    }
  ): void {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`;
    const recapName = `reconciliation_${operationType.toUpperCase()}`;
    const fileName = `${dateString}_${recapName}.xlsx`;

    // Create a new workbook and worksheet
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);

    ws['!merges'] = [];
    ws['!cols'] = [];

    let columnOffset = 0;

    for (let i = 0; i < reports.length; i++) {
      const currentConfig = this.configurations()[i];
      const reportData = reports[i];
      const styling = currentConfig.styling || {};
      // Extract column headers
      let headers =
        currentConfig?.transformations?.additionalRows?.[0]?.columns?.map(
          (col: any) => col.value
        ) || [];

      if (headers.length === 0 && reportData.length > 0) {
        headers = Object.keys(reportData[0]);
      }
      // Transform table data with proper number formatting
      let tableData = reportData.map((item: any) => {
        return Object.keys(item).map((key) => {
          const value = item[key];
          // Case 1: raw number
          if (typeof value === 'number') {
            if (value === 0) {
              return {
                v: '-',
                t: 's',
                s: {
                  alignment: { horizontal: 'right' },
                },
              };
            } else {
              return {
                v: value,
                t: 'n',
                z: '#,##0',
              };
            }
          }
          // Case 2: pre-formatted object with a number
          if (
            value &&
            typeof value === 'object' &&
            value.v !== undefined &&
            typeof value.v === 'number'
          ) {
            if (value.v === 0) {
              return {
                v: '-',
                t: 's',
                s: {
                  alignment: { horizontal: 'right' },
                },
              };
            } else {
              return value;
            }
          }
          // Default case
          return value;
        });
      });
      // Sort the tableData in descending order based on a specific numeric column
      if (tableData.length > 0) {
        // Determine which column to sort by (adjust index as needed)
        const sortColumnIndex = 1; //  the index of the numeric column
        tableData.sort((a: any[], b: any[]) => {
          const valA = a[sortColumnIndex]?.v || a[sortColumnIndex];
          const valB = b[sortColumnIndex]?.v || b[sortColumnIndex];

          // Handle '-' values (treated as 0 for sorting)
          const numA =
            valA === '-' ? 0 : typeof valA === 'object' ? valA?.v : valA;
          const numB =
            valB === '-' ? 0 : typeof valB === 'object' ? valB?.v : valB;

          return numB - numA; // Descending order
        });
      }
      // Report Title
      const reportTitle = currentConfig?.reportTitle || `Report ${i + 1}`;
      const columnSpan = headers.length - 1;
      // Apply title styling
      const titleCellRef = XLSX.utils.encode_cell({ r: 0, c: columnOffset });
      ws[titleCellRef] = {
        v: reportTitle,
        t: 's',
        s: {
          fill: {
            patternType: 'solid',
            fgColor: { rgb: styling.title?.bgColor },
          },
          font: {
            bold: styling.title?.bold !== false,
            color: { rgb: styling.title?.textColor },
          },
          alignment: {
            horizontal: styling.title?.horizontalAlignment,
          },
        },
      };
      // Merge title
      ws['!merges'].push({
        s: { r: 0, c: columnOffset },
        e: { r: 0, c: columnOffset + columnSpan },
      });
      // Apply header styling
      headers.forEach((header: string, idx: number) => {
        const headerCellRef = XLSX.utils.encode_cell({
          r: 1,
          c: columnOffset + idx,
        });
        ws[headerCellRef] = {
          v: header,
          t: 's',
          s: {
            fill: {
              patternType: 'solid',
              fgColor: { rgb: styling.headers?.bgColor },
            },
            font: {
              bold: styling.headers?.bold !== false,
              color: { rgb: styling.headers?.textColor },
            },
          },
        };
      });
      // Add table data to the sheet
      XLSX.utils.sheet_add_aoa(ws, tableData, {
        origin: { r: 2, c: columnOffset },
      });
      // Get all sum column configurations
      const sumColumns =
        currentConfig?.transformations?.additionalRows?.[1]?.columns?.filter(
          (col: any) => col.value?.function === 'sum'
        ) || [];
      // style last row of tables
      const lastRowIndex = tableData.length + 2;
      const DisplayLastRow = new Array(headers.length).fill('');
      DisplayLastRow[0] = 'Total';
      // Calculate totals for each sum column
      sumColumns.forEach((colConfig: any) => {
        const colIndex = colConfig.index;
        const lastRowtotal = tableData.reduce((sum: number, row: any) => {
          const cell = row[colIndex];
          const rawValue = typeof cell === 'object' ? cell?.v : cell;

          if (typeof rawValue === 'number') {
            return sum + rawValue;
          }

          return sum;
        }, 0);

        DisplayLastRow[colIndex] = { v: lastRowtotal, t: 'n', z: '#,##0' };
      });
      // Add to row to the worksheet
      XLSX.utils.sheet_add_aoa(ws, [DisplayLastRow], {
        origin: { r: lastRowIndex, c: columnOffset },
      });

      // Apply total row styling
      headers.forEach((_: any, idx: number) => {
        const cellRef = XLSX.utils.encode_cell({
          r: lastRowIndex,
          c: columnOffset + idx,
        });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: {
              patternType: 'solid',
              fgColor: { rgb: styling.lastRow?.bgColor || 'FFF2F2F2' },
            },
            font: {
              bold: styling.lastRow?.bold !== false,
              color: { rgb: styling.lastRow?.textColor || 'FF000000' },
            },
          };
        }
      });

      // Set column widths
      headers.forEach((_: any, idx: number) => {
        if (ws['!cols']) {
          ws['!cols'][columnOffset + idx] = { wch: 25 };
        }
      });

      columnOffset += headers.length + 2;
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Recapitulatif');
    // Excel spread sheet Tab (onglets) for transaction missing or mismatch
    Object.entries(reconciliationSheets).forEach(
      ([
        sheetName,
        { data: transactions, internalSourceName, externalSourceName },
      ]) => {
        const sheetData = [
          ['', `${internalSourceName}`, '', `${externalSourceName}`, '', ''],
          [
            'Référence',
            'Date',
            'Montant',
            'Montant',
            'Date',
            'Statut',
            'Ecart',
          ],
          ...transactions.map((trx) => {
            function formatAmount(amount: number | null | undefined): {
              v: string | number;
              t: string;
              z?: string;
            } {
              if (amount === null || amount === undefined || amount === 0) {
                return { v: '-', t: 's' };
              }
              return { v: amount, t: 'n', z: '#,##0' };
            }

            const difference =
              trx.amount1 && trx.amount2
                ? Math.abs(trx.amount1 - trx.amount2)
                : null;
            // Format amounts using the formatAmount function
            const row = [
              trx.reference?.toString() ?? '',
              trx.date2,
              formatAmount(trx.amount2),
              formatAmount(trx.amount1),
              trx.date1,
              trx.status,
            ];
            if (difference) {
              // Insert "Ecart value" at the 6th position (before 'Statut')
              row.splice(6, 0, formatAmount(difference));
            }

            return row;
          }),
        ];
        // Merge (fusion) cells for "Source 1 (B and C ) " and "Source 2 (D and E )"
        const wsReconciliation: XLSX.WorkSheet =
          XLSX.utils.aoa_to_sheet(sheetData);
        wsReconciliation['!merges'] = [
          { s: { r: 0, c: 1 }, e: { r: 0, c: 2 } },
          { s: { r: 0, c: 3 }, e: { r: 0, c: 4 } },
        ];
        const source1Cell = XLSX.utils.encode_cell({ r: 0, c: 1 });
        const source2Cell = XLSX.utils.encode_cell({ r: 0, c: 3 });

        if (wsReconciliation[source1Cell]) {
          wsReconciliation[source1Cell].s = {
            ...wsReconciliation[source1Cell].s,
            alignment: { horizontal: 'center', vertical: 'center' },
            font: { bold: true },
          };
        }

        if (wsReconciliation[source2Cell]) {
          wsReconciliation[source2Cell].s = {
            ...wsReconciliation[source2Cell].s,
            alignment: { horizontal: 'center', vertical: 'center' },
            font: { bold: true },
          };
        }

        const headerRowIndex = 1; // Second row (index 1)
        const headerRange = XLSX.utils.decode_range(wsReconciliation['!ref']!);
        for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
          const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
          if (wsReconciliation[cellRef]) {
            wsReconciliation[cellRef].s = {
              ...wsReconciliation[cellRef].s,
              fill: { fgColor: { rgb: 'FFCD639' } },
              font: { bold: true },
            };
          }
        }
        // Adjust row height
        wsReconciliation['!rows'] = [
          { hpx: 25 },
          { hpx: 20 },
          ...Array(transactions.length).fill({ hpx: 20 }),
        ];
        // Apply right alignment to the "Statut" column
        for (let R = headerRange.s.r; R <= headerRange.e.r; ++R) {
          const statusCell = XLSX.utils.encode_cell({ r: R, c: 5 });
          if (wsReconciliation[statusCell]) {
            wsReconciliation[statusCell].s = {
              ...wsReconciliation[statusCell].s,
              alignment: { horizontal: 'right' },
            };
          }
        }
        // Adjust column width
        wsReconciliation['!cols'] = [
          { wch: 25 },
          { wch: 18 },
          { wch: 18 },
          { wch: 18 },
          { wch: 18 },
          { wch: 18 },
          { wch: 12 },
        ];
        XLSX.utils.book_append_sheet(
          wb,
          wsReconciliation,
          sheetName.substring(0, 31)
        );
      }
    );
    XLSX.writeFile(wb, fileName);
  }

  launchReport(id: string) {
    return this.http.patch(`${this.apiUrl}/reconciliation-batches/${id}/launch-report`, {});
  }

  checkReport(id: string) {
    return this.http.patch<{
      status: string; available: boolean; presignedUrl?: string
    }>(
      `${this.apiUrl}/reconciliation-batches/${id}/check-report`,
      {}
    );
  }

}
